import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { AdminAvatar } from "@/components/admin/avatar";
import { AdminButton } from "@/components/admin/button";
import { Card } from "@/components/admin/card";
import { ChipFilter } from "@/components/admin/chip-filter";
import { AdminIcon } from "@/components/admin/icon";
import { PageHead } from "@/components/admin/page-head";
import { Tag } from "@/components/admin/tag";
import type { TagClass } from "@/lib/status";
import {
	supportApi,
	type TicketChannel,
	type TicketPriority,
	type TicketStatus,
} from "@/lib/support/support-api";

const PRIO_CLASS: Record<TicketPriority, TagClass> = {
	urgent: "danger",
	high: "warning",
	normal: "info",
	low: "neutral",
};
const PRIO_LABEL: Record<TicketPriority, string> = {
	urgent: "Urgente",
	high: "Haute",
	normal: "Normal",
	low: "Basse",
};
const STATUS_LABEL: Record<TicketStatus, string> = {
	open: "Ouvert",
	pending: "En attente",
	resolved: "Résolu",
	closed: "Fermé",
};
const CHANNEL_LABEL: Record<TicketChannel, string> = {
	whatsapp: "WhatsApp",
	email: "Email",
	phone: "Téléphone",
	web: "Web",
};

const STATUS_FILTERS: Array<{ id: TicketStatus | "all"; label: string }> = [
	{ id: "all", label: "Tous" },
	{ id: "open", label: "Ouverts" },
	{ id: "pending", label: "En attente" },
	{ id: "resolved", label: "Résolus" },
];

export function AdminSupportPage() {
	const qc = useQueryClient();
	const [filter, setFilter] = useState<TicketStatus | "all">("all");
	const [creating, setCreating] = useState(false);
	const [form, setForm] = useState({
		subject: "",
		customerLabel: "",
		channel: "whatsapp" as TicketChannel,
		priority: "normal" as TicketPriority,
	});

	const { data: stats } = useQuery({
		queryKey: ["support", "stats"],
		queryFn: () => supportApi.stats(),
	});
	const { data: list } = useQuery({
		queryKey: ["support", "tickets", filter],
		queryFn: () => supportApi.list(filter === "all" ? {} : { status: filter }),
	});
	const tickets = list?.items ?? [];

	const invalidate = () => {
		void qc.invalidateQueries({ queryKey: ["support"] });
	};
	const updateMut = useMutation({
		mutationFn: ({ id, status }: { id: string; status: TicketStatus }) =>
			supportApi.update(id, { status }),
		onSuccess: invalidate,
	});
	const createMut = useMutation({
		mutationFn: () => supportApi.create(form),
		onSuccess: () => {
			setForm({
				subject: "",
				customerLabel: "",
				channel: "whatsapp",
				priority: "normal",
			});
			setCreating(false);
			invalidate();
		},
	});

	return (
		<>
			<PageHead
				title="Support CSR"
				sub={`${stats?.open ?? 0} tickets ouverts · ${stats?.urgent ?? 0} urgents · ${stats?.unassigned ?? 0} non assignés`}
				actions={
					<AdminButton variant="primary" onClick={() => setCreating((v) => !v)}>
						<AdminIcon n="plus" s={14} />
						Nouveau ticket
					</AdminButton>
				}
			/>

			<div
				className="kpi-grid"
				style={{ gridTemplateColumns: "repeat(5, 1fr)" }}
			>
				<div className="kpi accent">
					<div className="kpi-l">Ouverts</div>
					<div className="kpi-v tn">{stats?.open ?? 0}</div>
				</div>
				<div className="kpi warning">
					<div className="kpi-l">Urgents</div>
					<div className="kpi-v tn">{stats?.urgent ?? 0}</div>
					<div className="kpi-foot muted">SLA &lt; 1h</div>
				</div>
				<div className="kpi">
					<div className="kpi-l">En attente</div>
					<div className="kpi-v tn">{stats?.pending ?? 0}</div>
				</div>
				<div className="kpi success">
					<div className="kpi-l">Résolus</div>
					<div className="kpi-v tn">{stats?.resolved ?? 0}</div>
				</div>
				<div className="kpi forest">
					<div className="kpi-l">SLA dépassé</div>
					<div className="kpi-v tn">{stats?.slaBreached ?? 0}</div>
					<div className="kpi-foot muted">
						{stats?.unassigned ?? 0} non assignés
					</div>
				</div>
			</div>

			{creating && (
				<Card>
					<div
						style={{
							padding: 16,
							display: "grid",
							gap: 10,
							gridTemplateColumns: "2fr 1.4fr 1fr 1fr auto",
							alignItems: "end",
						}}
					>
						<label style={{ display: "grid", gap: 4 }}>
							<span className="muted" style={{ fontSize: 11 }}>
								Sujet *
							</span>
							<input
								className="field-input"
								value={form.subject}
								onChange={(e) =>
									setForm((f) => ({ ...f, subject: e.target.value }))
								}
								placeholder="Décrire la demande"
							/>
						</label>
						<label style={{ display: "grid", gap: 4 }}>
							<span className="muted" style={{ fontSize: 11 }}>
								Client
							</span>
							<input
								className="field-input"
								value={form.customerLabel}
								onChange={(e) =>
									setForm((f) => ({ ...f, customerLabel: e.target.value }))
								}
								placeholder="Nom / téléphone"
							/>
						</label>
						<label style={{ display: "grid", gap: 4 }}>
							<span className="muted" style={{ fontSize: 11 }}>
								Canal
							</span>
							<select
								className="field-input"
								value={form.channel}
								onChange={(e) =>
									setForm((f) => ({
										...f,
										channel: e.target.value as TicketChannel,
									}))
								}
							>
								{(["whatsapp", "email", "phone", "web"] as TicketChannel[]).map(
									(c) => (
										<option key={c} value={c}>
											{CHANNEL_LABEL[c]}
										</option>
									),
								)}
							</select>
						</label>
						<label style={{ display: "grid", gap: 4 }}>
							<span className="muted" style={{ fontSize: 11 }}>
								Priorité
							</span>
							<select
								className="field-input"
								value={form.priority}
								onChange={(e) =>
									setForm((f) => ({
										...f,
										priority: e.target.value as TicketPriority,
									}))
								}
							>
								{(["low", "normal", "high", "urgent"] as TicketPriority[]).map(
									(p) => (
										<option key={p} value={p}>
											{PRIO_LABEL[p]}
										</option>
									),
								)}
							</select>
						</label>
						<AdminButton
							variant="primary"
							disabled={!form.subject.trim() || createMut.isPending}
							onClick={() => createMut.mutate()}
						>
							{createMut.isPending ? "…" : "Créer"}
						</AdminButton>
					</div>
				</Card>
			)}

			<Card>
				<div className="toolbar">
					<div className="filter-bar" style={{ marginBottom: 0 }}>
						{STATUS_FILTERS.map((f) => (
							<ChipFilter
								key={f.id}
								on={filter === f.id}
								onClick={() => setFilter(f.id)}
							>
								{f.label}
							</ChipFilter>
						))}
					</div>
				</div>
				<div className="table-wrap" style={{ margin: 0 }}>
					<table className="table">
						<thead>
							<tr>
								<th>Ticket</th>
								<th>Sujet</th>
								<th>Client</th>
								<th>Canal</th>
								<th>Priorité</th>
								<th>SLA</th>
								<th>Assigné</th>
								<th>Statut</th>
							</tr>
						</thead>
						<tbody>
							{tickets.length === 0 && (
								<tr>
									<td
										colSpan={8}
										className="muted"
										style={{ textAlign: "center", padding: 24 }}
									>
										Aucun ticket.
									</td>
								</tr>
							)}
							{tickets.map((t) => {
								const overdue =
									t.slaDueAt &&
									new Date(t.slaDueAt).getTime() < Date.now() &&
									t.status !== "resolved" &&
									t.status !== "closed";
								return (
									<tr key={t.id}>
										<td>
											<span className="mono">{t.number}</span>
										</td>
										<td>
											<strong style={{ fontSize: 12 }}>{t.subject}</strong>
										</td>
										<td>
											{t.customerLabel ?? <span className="muted">—</span>}
										</td>
										<td>
											<Tag variant="neutral" noDot>
												{CHANNEL_LABEL[t.channel]}
											</Tag>
										</td>
										<td>
											<Tag variant={PRIO_CLASS[t.priority]}>
												{PRIO_LABEL[t.priority]}
											</Tag>
										</td>
										<td className="tn">
											<strong
												style={{
													color: overdue ? "var(--a-danger)" : "inherit",
												}}
											>
												{t.slaDueAt
													? new Date(t.slaDueAt).toLocaleString("fr-FR", {
															day: "2-digit",
															month: "2-digit",
															hour: "2-digit",
															minute: "2-digit",
														})
													: "—"}
											</strong>
										</td>
										<td>
											{t.assigneeLabel ? (
												<div
													style={{
														display: "flex",
														alignItems: "center",
														gap: 6,
													}}
												>
													<AdminAvatar name={t.assigneeLabel} />
													{t.assigneeLabel}
												</div>
											) : (
												<span className="muted">Non assigné</span>
											)}
										</td>
										<td>
											<select
												className="field-input"
												style={{ padding: "4px 8px", fontSize: 12 }}
												value={t.status}
												onChange={(e) =>
													updateMut.mutate({
														id: t.id,
														status: e.target.value as TicketStatus,
													})
												}
											>
												{(
													[
														"open",
														"pending",
														"resolved",
														"closed",
													] as TicketStatus[]
												).map((s) => (
													<option key={s} value={s}>
														{STATUS_LABEL[s]}
													</option>
												))}
											</select>
										</td>
									</tr>
								);
							})}
						</tbody>
					</table>
				</div>
			</Card>
		</>
	);
}
